import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Receipt, TrendingUp, DollarSign, Trash2, Search } from "lucide-react";

function nzd(n) { return new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD", maximumFractionDigits: 0 }).format(n || 0); }
function fmtDate(iso) { try { return new Date(iso).toLocaleDateString("en-NZ", { day: "2-digit", month: "short", year: "numeric" }); } catch { return iso; } }

export default function Sales() {
  const { user } = useAuth();
  const [sales, setSales] = useState([]);
  const [q, setQ] = useState("");
  const canUnmark = user?.role === "admin" || user?.role === "manager";

  const load = async () => {
    const { data } = await api.get("/sales");
    setSales(data);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!q) return sales;
    const s = q.toLowerCase();
    return sales.filter((r) =>
      (r.customer_name || "").toLowerCase().includes(s) ||
      (r.vehicle_snapshot?.stock_no || "").toLowerCase().includes(s) ||
      (`${r.vehicle_snapshot?.make} ${r.vehicle_snapshot?.model}`).toLowerCase().includes(s) ||
      (r.vehicle_snapshot?.rego || "").toLowerCase().includes(s)
    );
  }, [sales, q]);

  const stats = useMemo(() => {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const mtd = sales.filter((s) => new Date(s.sale_date) >= monthStart);
    return {
      total: sales.length,
      totalRevenue: sales.reduce((a, s) => a + (s.sale_price || 0), 0),
      totalMargin: sales.reduce((a, s) => a + (s.gross_margin || 0), 0),
      mtdCount: mtd.length,
      mtdRevenue: mtd.reduce((a, s) => a + (s.sale_price || 0), 0),
      mtdMargin: mtd.reduce((a, s) => a + (s.gross_margin || 0), 0),
    };
  }, [sales]);

  const unmark = async (id) => {
    if (!window.confirm("Reverse this sale? Vehicle will move back to Available.")) return;
    try {
      await api.delete(`/sales/${id}`);
      toast.success("Sale reversed");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Sales</h1>
          <p className="text-sm text-slate-500 mt-1">Every vehicle you've sold, with buyer, price and margin.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="card-elevated p-5">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Total Sales</div>
              <div className="text-3xl font-bold text-slate-900 mt-2" style={{ fontFamily: "Outfit" }}>{stats.total}</div>
              <div className="text-xs text-slate-500 mt-1">{stats.mtdCount} this month</div>
            </div>
            <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center"><Receipt className="w-5 h-5" /></div>
          </div>
        </div>
        <div className="card-elevated p-5">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Revenue</div>
              <div className="text-3xl font-bold text-slate-900 mt-2" style={{ fontFamily: "Outfit" }}>{nzd(stats.totalRevenue)}</div>
              <div className="text-xs text-slate-500 mt-1">MTD {nzd(stats.mtdRevenue)}</div>
            </div>
            <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center"><DollarSign className="w-5 h-5" /></div>
          </div>
        </div>
        <div className="card-elevated p-5">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Gross Margin</div>
              <div className={`text-3xl font-bold mt-2 ${stats.totalMargin >= 0 ? "text-emerald-700" : "text-rose-700"}`} style={{ fontFamily: "Outfit" }}>{nzd(stats.totalMargin)}</div>
              <div className="text-xs text-slate-500 mt-1">MTD {nzd(stats.mtdMargin)}</div>
            </div>
            <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center"><TrendingUp className="w-5 h-5" /></div>
          </div>
        </div>
      </div>

      <div className="card-elevated p-4 relative">
        <Search className="w-4 h-4 absolute left-7 top-1/2 -translate-y-1/2 text-slate-400" />
        <input data-testid="sales-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search buyer, stock #, make/model or rego…" className="w-full h-10 pl-10 pr-4 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
      </div>

      <div className="card-elevated overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-widest text-slate-500 bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left py-3 pl-4">Sale Date</th>
              <th className="text-left">Stock #</th>
              <th className="text-left">Vehicle</th>
              <th className="text-left">Buyer</th>
              <th className="text-left">Rep</th>
              <th className="text-right">Sale Price</th>
              <th className="text-right">Margin</th>
              <th className="text-left pl-4">Payment</th>
              {canUnmark && <th className="pr-4"></th>}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={canUnmark ? 9 : 8} className="py-16 text-center text-slate-400">No sales recorded yet. Mark a vehicle as sold from Inventory to add one here.</td></tr>
            )}
            {filtered.map((s) => (
              <tr key={s.id} data-testid={`sale-row-${s.id}`} className="border-b border-slate-100 row-hover">
                <td className="py-3 pl-4 text-slate-700 whitespace-nowrap">{fmtDate(s.sale_date)}</td>
                <td className="font-mono text-xs text-slate-700">{s.vehicle_snapshot?.stock_no}</td>
                <td>
                  <div className="font-semibold text-slate-900">{s.vehicle_snapshot?.year} {s.vehicle_snapshot?.make} {s.vehicle_snapshot?.model}</div>
                  <div className="text-xs font-mono text-slate-500">{s.vehicle_snapshot?.rego || "—"}</div>
                </td>
                <td className="font-semibold text-slate-900">{s.customer_name}</td>
                <td className="text-slate-600 text-xs">{s.sales_rep_name || "—"}</td>
                <td className="text-right font-mono font-semibold text-slate-900">{nzd(s.sale_price)}</td>
                <td className={`text-right font-mono font-semibold ${s.gross_margin >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{nzd(s.gross_margin)}</td>
                <td className="pl-4"><span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded bg-slate-100 text-slate-700">{s.payment_method}</span></td>
                {canUnmark && (
                  <td className="pr-4 text-right">
                    <button onClick={() => unmark(s.id)} data-testid={`sale-unmark-${s.id}`} className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded"><Trash2 className="w-4 h-4" /></button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
